import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useTheme } from "@/contexts/ThemeContext";
import { getErrorMessage } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Loader2, ArrowRight, ArrowLeft, Check, Mail, Building2, MapPin, 
  User, Phone, AlertCircle
} from "lucide-react";

// Australian States
const AU_STATES = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NT", label: "Northern Territory" },
];

// Google Maps Script Loader
const loadGoogleMapsScript = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
      return;
    }
    
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.google.maps));
      return;
    }
    
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      reject(new Error("Google Maps API key not configured"));
      return;
    }
    
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export default function Register() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Step 1: Email & Password
    email: "",
    password: "",
    confirmPassword: "",
    
    // Step 2: Business Details
    business_name: "",
    abn: "",
    tier: "BRONZE",
    
    // Head Office Address
    street_address: "",
    suburb: "",
    state: "",
    postcode: "",
    country: "Australia",
    latitude: 0,
    longitude: 0,
    
    // Operating Suburbs
    operating_suburbs: [],
    
    // Primary Contact
    primary_contact_first_name: "",
    primary_contact_last_name: "",
    primary_contact_email: "",
    primary_contact_phone: "",
    
    // Emergency Contact
    emergency_contact_name: "",
    emergency_contact_email: "",
    emergency_contact_phone: "",
    

    // Terms acceptance
    terms_accepted: false,
    
  });

  const [errors, setErrors] = useState({});
  const [verificationSent, setVerificationSent] = useState(false);
  

  // Initialize Google Places Autocomplete for Head Office Address
  useEffect(() => {
    if (step === 2 && addressInputRef.current) {
      loadGoogleMapsScript().then((maps) => {
        if (!autocompleteRef.current && addressInputRef.current) {
          autocompleteRef.current = new maps.places.Autocomplete(addressInputRef.current, {
            types: ['address'],
            componentRestrictions: { country: 'au' }
          });
          
          autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            if (place.geometry) {
              const addressComponents = place.address_components || [];
              let streetNumber = '';
              let streetName = '';
              let suburb = '';
              let state = '';
              let postcode = '';
              
              addressComponents.forEach(component => {
                const types = component.types;
                if (types.includes('street_number')) streetNumber = component.long_name;
                if (types.includes('route')) streetName = component.long_name;
                if (types.includes('locality')) suburb = component.long_name;
                if (types.includes('administrative_area_level_1')) state = component.short_name || component.long_name || '';
                if (types.includes('postal_code')) postcode = component.long_name;
              });

              // Google may return the full state name (e.g. "New South Wales") rather
              // than the abbreviation; normalise to the AU_STATES value the <Select>
              // expects (e.g. "NSW") so the State field reliably auto-populates.
              if (state) {
                const matchedState = AU_STATES.find(
                  (s) => s.value.toLowerCase() === state.toLowerCase() || s.label.toLowerCase() === state.toLowerCase()
                );
                state = matchedState ? matchedState.value : "";
              }
              
              setFormData(prev => ({
                ...prev,
                street_address: `${streetNumber} ${streetName}`.trim(),
                suburb,
                state,
                postcode,
                latitude: place.geometry.location.lat(),
                longitude: place.geometry.location.lng(),
              }));
            }
          });
        }
      }).catch(console.error);
    }
  }, [step]);


  // Check email availability — shared by the debounced live indicator and the
  // Next-button gate, so we never advance on a stale or in-flight result.
  const checkEmailAvailability = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailAvailable(null);
      return null;
    }
    setEmailChecking(true);
    try {
      const response = await axios.get(`${API}/auth/check-email?email=${encodeURIComponent(email)}`);
      setEmailAvailable(response.data.available);
      return response.data.available;
    } catch (error) {
      setEmailAvailable(null);
      return null;
    } finally {
      setEmailChecking(false);
    }
  };

  // Debounced live check that drives the inline check / cross email indicator
  useEffect(() => {
    const timer = setTimeout(() => { checkEmailAvailability(formData.email); }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email]);

  // Auto-fill primary contact email from account email
  useEffect(() => {
    if (step === 2 && !formData.primary_contact_email && formData.email) {
      setFormData(prev => ({ ...prev, primary_contact_email: formData.email }));
    }
  }, [step, formData.email, formData.primary_contact_email]);

  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!formData.email) newErrors.email = "Email is required";
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
      else if (emailAvailable === false) newErrors.email = "Email is already registered";
      if (!formData.password) newErrors.password = "Password is required";
      else if (formData.password.length < 8) newErrors.password = "Password must be at least 8 characters";
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Passwords don't match";
    }
    
    if (currentStep === 2) {
      // Business Details
      if (!formData.business_name) newErrors.business_name = "Business name is required";
      if (!formData.abn) newErrors.abn = "ABN is required";
      else {
        const abnClean = formData.abn.replace(/\s|-/g, '');
        if (!/^\d{11}$/.test(abnClean)) newErrors.abn = "ABN must be 11 digits";
      }
      
      // Head Office Address
      if (!formData.street_address) newErrors.street_address = "Street address is required";
      if (!formData.suburb) newErrors.suburb = "Suburb is required";
      if (!formData.state) newErrors.state = "State is required";
      if (!formData.postcode) newErrors.postcode = "Postcode is required";
      
      // Primary Contact
      if (!formData.primary_contact_first_name) newErrors.primary_contact_first_name = "First name is required";
      if (!formData.primary_contact_last_name) newErrors.primary_contact_last_name = "Last name is required";
      if (!formData.primary_contact_email) newErrors.primary_contact_email = "Email is required";
      if (!formData.primary_contact_phone) newErrors.primary_contact_phone = "Phone is required";
      
      // Emergency Contact (optional) — if an email is provided, it must be a different person than the primary contact
      if (
        formData.emergency_contact_email &&
        formData.emergency_contact_email.trim().toLowerCase() === formData.primary_contact_email.trim().toLowerCase()
      ) {
        newErrors.emergency_contact_email = "Emergency contact should be a different person from your primary contact";
      }
      

      // Terms & Conditions
      if (!formData.terms_accepted) newErrors.terms_accepted = "You must agree to the Terms and Conditions and Privacy Policy";
      
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep(step)) return;

    // Gate step 1 on a freshly-resolved availability check tied to the CURRENT
    // email — prevents advancing with an in-use email during the debounce window.
    if (step === 1) {
      const available = await checkEmailAvailability(formData.email);
      if (available === false) {
        setErrors((prev) => ({ ...prev, email: "Email is already registered" }));
        return;
      }
      if (available !== true) {
        setErrors((prev) => ({ ...prev, email: "Couldn't verify email availability — please try again" }));
        return;
      }
    }

    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setLoading(true);
    try {
      const lat = formData.latitude || -33.8688;
      const lng = formData.longitude || 151.2093;
      
      await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        business_name: formData.business_name,
        abn: formData.abn.replace(/\s|-/g, ''),
        tier: formData.tier,
        street_address: formData.street_address,
        suburb: formData.suburb,
        state: formData.state,
        postcode: formData.postcode,
        country: formData.country,
        latitude: lat,
        longitude: lng,
        operating_suburbs: formData.operating_suburbs,
        primary_contact_first_name: formData.primary_contact_first_name,
        primary_contact_last_name: formData.primary_contact_last_name,
        primary_contact_email: formData.primary_contact_email,
        primary_contact_phone: formData.primary_contact_phone,
        emergency_contact_name: formData.emergency_contact_name || undefined,
        emergency_contact_email: formData.emergency_contact_email || undefined,
        emergency_contact_phone: formData.emergency_contact_phone || undefined,
        terms_accepted: formData.terms_accepted,
      });
      
      setVerificationSent(true);
      toast.success("Registration successful! Please verify your email.");
    } catch (error) {
      const errorMessage = getErrorMessage(error.response?.data?.detail, "Registration failed");
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatABN = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('61')) {
      return '+' + digits.slice(0, 2) + ' ' + digits.slice(2, 5) + ' ' + digits.slice(5, 8) + ' ' + digits.slice(8);
    }
    if (digits.startsWith('0')) {
      return digits.slice(0, 4) + ' ' + digits.slice(4, 7) + ' ' + digits.slice(7);
    }
    return value;
  };

  // Verification Success Screen
  if (verificationSent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-green-500/10 via-transparent to-transparent rounded-full blur-3xl" />
          <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-primary/10 via-transparent to-transparent rounded-full blur-3xl" />
        </div>

        <Card className="w-full max-w-md glass-heavy animate-fade-in relative z-10" data-testid="verification-card">
          <CardHeader className="space-y-4 text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
              <Mail className="h-8 w-8 text-green-500" />
            </div>
            <CardTitle className="text-2xl">Check Your Email</CardTitle>
            <CardDescription>
              We've sent a verification link to <strong>{formData.email}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 rounded-lg bg-secondary/30 border border-border">
              <p className="text-sm text-muted-foreground">
                Click the link in your email to activate your account. The link expires in 24 hours.
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={() => navigate('/login')}>
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/10 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-purple-500/10 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      <Card className={`w-full ${step === 1 ? 'max-w-md' : 'max-w-3xl'} glass-heavy animate-fade-in relative z-10`} data-testid="register-card">
        <CardHeader className="space-y-4 text-center">
          <Link to="/login">
            <img 
              src="/booma-partner-portal-trans-bg.png"
              alt="Booma Partner Portal"
              className="h-12 w-auto mx-auto dark:invert"
            />
          </Link>
          <CardTitle className="text-2xl">
            {step === 1 ? "Create Your Account" : "Business Details"}
          </CardTitle>
          <CardDescription>
            {step === 1
              ? "Reach customers while they're deciding where to go next."
              : "Tell us about your business"
            }
          </CardDescription>
          
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* STEP 1: Email & Password */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <div className="relative">
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@business.com.au"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`h-12 pr-10 ${errors.email ? 'border-red-500' : ''}`}
                      data-testid="register-email"
                    />
                    {emailChecking && (
                      <Loader2 className="absolute right-3 top-3.5 h-5 w-5 animate-spin text-muted-foreground" />
                    )}
                    {!emailChecking && emailAvailable === true && (
                      <Check className="absolute right-3 top-3.5 h-5 w-5 text-green-500" />
                    )}
                    {!emailChecking && emailAvailable === false && (
                      <AlertCircle className="absolute right-3 top-3.5 h-5 w-5 text-red-500" />
                    )}
                  </div>
                  {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                </div>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Minimum 8 characters"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`h-12 ${errors.password ? 'border-red-500' : ''}`}
                      data-testid="register-password"
                    />
                    {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className={`h-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                      data-testid="register-confirm-password"
                    />
                    {errors.confirmPassword && <p className="text-xs text-red-500">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: All Business Details */}
          {step === 2 && (
            <div className="space-y-8" data-testid="step-2">
              {/* Business Details Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Business Details
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="business_name">Business Name *</Label>
                    <Input
                      id="business_name"
                      placeholder="Your Business Pty Ltd"
                      value={formData.business_name}
                      onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                      className={`h-12 ${errors.business_name ? 'border-red-500' : ''}`}
                      data-testid="register-business-name"
                    />
                    {errors.business_name && <p className="text-xs text-red-500">{errors.business_name}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="abn">ABN *</Label>
                    <Input
                      id="abn"
                      placeholder="XX XXX XXX XXX"
                      value={formData.abn}
                      onChange={(e) => setFormData({ ...formData, abn: formatABN(e.target.value) })}
                      className={`h-12 font-mono ${errors.abn ? 'border-red-500' : ''}`}
                      data-testid="register-abn"
                    />
                    {errors.abn && <p className="text-xs text-red-500">{errors.abn}</p>}
                  </div>
                </div>
              </div>

              {/* Head Office Address */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Head Office / Postal Address
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Search Address</Label>
                    <Input
                      ref={addressInputRef}
                      placeholder="Start typing your address..."
                      className="h-12"
                      data-testid="register-address-autocomplete"
                    />
                    <p className="text-xs text-muted-foreground">Select from dropdown to auto-fill fields below</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="street_address">Street Address *</Label>
                      <Input
                        id="street_address"
                        value={formData.street_address}
                        onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                        className={`h-12 ${errors.street_address ? 'border-red-500' : ''}`}
                      />
                      {errors.street_address && <p className="text-xs text-red-500">{errors.street_address}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="suburb">Suburb *</Label>
                      <Input
                        id="suburb"
                        value={formData.suburb}
                        onChange={(e) => setFormData({ ...formData, suburb: e.target.value })}
                        className={`h-12 ${errors.suburb ? 'border-red-500' : ''}`}
                      />
                      {errors.suburb && <p className="text-xs text-red-500">{errors.suburb}</p>}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Select value={formData.state} onValueChange={(v) => setFormData({ ...formData, state: v })}>
                        <SelectTrigger className={`h-12 ${errors.state ? 'border-red-500' : ''}`}>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {AU_STATES.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.state && <p className="text-xs text-red-500">{errors.state}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="postcode">Postcode *</Label>
                      <Input
                        id="postcode"
                        value={formData.postcode}
                        onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                        className={`h-12 ${errors.postcode ? 'border-red-500' : ''}`}
                        maxLength={4}
                      />
                      {errors.postcode && <p className="text-xs text-red-500">{errors.postcode}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Primary Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Primary Contact
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      placeholder="John"
                      value={formData.primary_contact_first_name}
                      onChange={(e) => setFormData({ ...formData, primary_contact_first_name: e.target.value })}
                      className={`h-12 ${errors.primary_contact_first_name ? 'border-red-500' : ''}`}
                    />
                    {errors.primary_contact_first_name && <p className="text-xs text-red-500">{errors.primary_contact_first_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      placeholder="Smith"
                      value={formData.primary_contact_last_name}
                      onChange={(e) => setFormData({ ...formData, primary_contact_last_name: e.target.value })}
                      className={`h-12 ${errors.primary_contact_last_name ? 'border-red-500' : ''}`}
                    />
                    {errors.primary_contact_last_name && <p className="text-xs text-red-500">{errors.primary_contact_last_name}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      placeholder="john@business.com.au"
                      value={formData.primary_contact_email}
                      onChange={(e) => setFormData({ ...formData, primary_contact_email: e.target.value })}
                      className={`h-12 ${errors.primary_contact_email ? 'border-red-500' : ''}`}
                    />
                    {errors.primary_contact_email && <p className="text-xs text-red-500">{errors.primary_contact_email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      placeholder="04XX XXX XXX"
                      value={formData.primary_contact_phone}
                      onChange={(e) => setFormData({ ...formData, primary_contact_phone: formatPhone(e.target.value) })}
                      className={`h-12 ${errors.primary_contact_phone ? 'border-red-500' : ''}`}
                    />
                    {errors.primary_contact_phone && <p className="text-xs text-red-500">{errors.primary_contact_phone}</p>}
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Phone className="h-5 w-5 text-primary" />
                  Emergency Contact
                  <span className="text-sm font-normal text-muted-foreground">- Optional</span>
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input
                      placeholder="Jane Smith"
                      value={formData.emergency_contact_name}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                      className={`h-12 ${errors.emergency_contact_name ? 'border-red-500' : ''}`}
                    />
                    {errors.emergency_contact_name && <p className="text-xs text-red-500">{errors.emergency_contact_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="jane@business.com.au"
                      value={formData.emergency_contact_email}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_email: e.target.value })}
                      className={`h-12 ${errors.emergency_contact_email ? 'border-red-500' : ''}`}
                    />
                    {errors.emergency_contact_email && <p className="text-xs text-red-500">{errors.emergency_contact_email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      type="tel"
                      placeholder="04XX XXX XXX"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => setFormData({ ...formData, emergency_contact_phone: formatPhone(e.target.value) })}
                      className={`h-12 ${errors.emergency_contact_phone ? 'border-red-500' : ''}`}
                    />
                    {errors.emergency_contact_phone && <p className="text-xs text-red-500">{errors.emergency_contact_phone}</p>}
                  </div>
                </div>
              </div>


              {/* Terms & Conditions */}
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={formData.terms_accepted}
                    onCheckedChange={(c) => setFormData({ ...formData, terms_accepted: c === true })}
                    className="mt-0.5"
                    data-testid="register-terms"
                  />
                  <Label htmlFor="terms" className="text-sm font-normal leading-relaxed text-muted-foreground cursor-pointer">
                    I agree to the{" "}
                    <a href="https://www.boomahq.com/website-terms-of-use" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">Terms and Conditions</a>
                    {" "}and{" "}
                    <a href="https://www.boomahq.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">Privacy Policy</a>.
                  </Label>
                </div>
                {errors.terms_accepted && <p className="text-xs text-red-500">{errors.terms_accepted}</p>}
              </div>
            </div>
          )}

          {/* Navigation — step 1 is a vertical form (full-width Next + login link
              below); step 2 keeps the Back / Create Account row. */}
          {step === 1 ? (
            <div className="space-y-4 pt-2">
              <Button onClick={handleNext} disabled={emailChecking} className="w-full h-12" data-testid="next-btn">
                {emailChecking ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Checking email...
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to="/login" className="text-primary font-medium hover:underline">Log in</Link>
              </p>
            </div>
          ) : (
            <div className="flex justify-between pt-6 border-t border-border">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={loading} data-testid="submit-btn">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Create Account
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
